import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoCard } from '@/components/InfoCard/InfoCard'
import { Skeleton } from '@/components/Skeleton/Skeleton'
import { useAddonComparisonQuery } from '@/services/graphql/generated'
import { useErrorMessage } from '@/utils/useErrorMessage'
import { useResponsiveVariant } from '@/utils/useResponsiveVariant'
import { DesktopComparisonTable } from 'ui/src/components/ComparisonTable/DesktopComparisonTable'
import { MobileComparisonTable } from 'ui/src/components/ComparisonTable/MobileComparisonTable'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { Text } from 'ui/src/components/Text/Text'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { addonComparisonToTable } from './addonComparisonToTable'
import { noticeCard } from './AddonComparisonDialogBody.css'

type Props = {
  baseTermsVersion: string
  addonTermsVersion: string
}

export function AddonComparisonDialogBody({ baseTermsVersion, addonTermsVersion }: Props) {
  const { t } = useTranslation('purchase-form')
  const { loading, data, error } = useAddonComparisonQuery({ variables: { baseTermsVersion, addonTermsVersion } })
  const errorMessage = useErrorMessage(error)
  const variant = useResponsiveVariant('sm')
  // NOTE: We don't include LargeDialog.Content here since it's always mounted, and we want to delay loading the data
  // until dialog is opened
  let content: ReactNode = null
  if (loading) {
    content = <Skeleton style={{ height: '50vh' }} />
  } else if (error) {
    content = <Text color="textPrimary">{errorMessage}</Text>
  } else if (data?.addonComparison) {
    const table = addonComparisonToTable(data.addonComparison, t('COMPARE_ADDON_BASE_TITLE'))
    const addonColumn = table.head.at(-1)!
    content = (
      <>
        {variant === 'mobile' && <MobileComparisonTable {...table} defaultSelectedColumn={addonColumn} />}
        {variant === 'desktop' && <DesktopComparisonTable {...table} />}
      </>
    )
  }
  return (
    <div>
      <LargeDialog.Heading as="h2">{t('COMPARE_ADDON_DIALOG_TITLE')}</LargeDialog.Heading>
      <LargeDialog.Heading as="h3" color="textSecondary" className={sprinkles({ marginBottom: 'md' })}>
        {t('COMPARE_ADDON_DIALOG_SUBTITLE')}
      </LargeDialog.Heading>
      {content}
      <InfoCard className={noticeCard}>
        <Text size="xs" color="signalBlueText">
          {t('COMPARE_ADDON_DISCLAIMER')}
        </Text>
      </InfoCard>
    </div>
  )
}
